"use client";

import * as React from "react";
import { Input as InputPrimitive } from "@base-ui/react/input";
import { cn } from "@/lib/utils";

function Input({
  className,
  type,
  inputMode,
  onPointerDown,
  onFocus,
  onMouseUp,
  ...props
}: React.ComponentProps<"input">) {
  // Numeric fields select their current value on focus, so typing a new
  // number replaces the old one instead of appending digits to it.
  const selectOnFocus =
    type === "number" || inputMode === "decimal" || inputMode === "numeric";
  // True while a pointer press is focusing the input. WebKit collapses the
  // focus-time selection to a caret when that press is released, so its
  // mouseup is cancelled.
  const focusingPointer = React.useRef(false);

  // Coarse pointers get a 44px tap target. It keys on data-slot="input" so an
  // input-group control, which carries its own slot, sizes to its group.
  return (
    <InputPrimitive
      type={type}
      inputMode={inputMode}
      data-slot="input"
      className={cn(
        "border-input file:text-foreground placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 disabled:bg-input/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:bg-input/30 dark:disabled:bg-input/80 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 h-8 w-full min-w-0 rounded-lg border bg-transparent px-2.5 py-1 text-base transition-colors outline-none file:inline-flex file:h-6 file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-3 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:ring-3 md:text-sm [@media(pointer:coarse)]:data-[slot=input]:min-h-11",
        className,
      )}
      onPointerDown={(e) => {
        focusingPointer.current =
          e.currentTarget !== e.currentTarget.ownerDocument.activeElement;
        onPointerDown?.(e);
      }}
      onFocus={(e) => {
        onFocus?.(e);
        if (selectOnFocus) e.currentTarget.select();
      }}
      onMouseUp={(e) => {
        onMouseUp?.(e);
        if (selectOnFocus && focusingPointer.current) e.preventDefault();
        focusingPointer.current = false;
      }}
      {...props}
    />
  );
}

export { Input };
